import {
  Alert,
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Snackbar,
  SvgIcon,
  TextField,
  Typography,
} from '@mui/material';
import { FC, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { SearchLg } from '../../../assets';
import { Section } from '../../../components/ui/Section';
import Pagination from '../../../components/forms/Pagination';
import ProductsList from './ProductsList';
import { MarketItem, MarketItemFilterOptions, Instance, Market, client } from '../../../app/services/api/orderManagementClient';
import useLogError from '../../../hooks/useLogError';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
type FilterChangeCallback = (instance: string, market: string) => void;

type Props = {
  onCreateNew: () => void;
  onFilterChange?: FilterChangeCallback;
  initialInstance?: string;
  initialMarket?: string;
}

const ProductsTab: FC<Props> = ({ onCreateNew, onFilterChange, initialInstance, initialMarket }) => {
  const { t } = useTranslation('productManagement');
  const { logError } = useLogError();

  const [products, setProducts] = useState<MarketItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchInput, setSearchInput] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedInstance, setSelectedInstance] = useState<string>('');
  const [selectedMarket, setSelectedMarket] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [instances, setInstances] = useState<Instance[]>([]);
  const [markets, setMarkets] = useState<Market[]>([]);
  
  // Snackbar states
  const [showSubmitStatusMessage, setShowSubmitStatusMessage] = useState<boolean>(false);
  const [isSubmittedSuccessfully, setIsSubmittedSuccessfully] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const pageSize = 20;

  useEffect(() => {
    loadInstances();
  }, []);

  useEffect(() => {
    if (initialInstance) {
      setSelectedInstance(initialInstance);
    }
    if (initialMarket) {
      setSelectedMarket(initialMarket);
    }
  }, [initialInstance, initialMarket]);

  useEffect(() => {
    if (selectedInstance) {
      loadMarkets(selectedInstance);
    } else {
      setMarkets([]);
      setSelectedMarket('');
    }
  }, [selectedInstance]);

  useEffect(() => {
    if (selectedInstance && selectedMarket) {
      loadProducts();
    } else {
      setProducts([]);
      setTotalPages(0);
      setTotalItems(0);
    }
  }, [currentPage, searchTerm, selectedInstance, selectedMarket]);

  const loadInstances = async () => {
    try {
      const response = await client.getInstances({
        pageNumber: 0,
        pageSize: 2147483647,
      });
      
      if (response.success && response.data) {
        setInstances(response.data.items || []);
      }
    } catch (error) {
      logError('Failed to load instances');
    }
  };

  const loadMarkets = async (instanceId: string) => {
    try {
      const response = await client.getMarkets({
        companyId: instanceId,
        pageNumber: 0,
        pageSize: 2147483647,
      });
      
      if (response.success && response.data) {
        setMarkets(response.data.items || []);
      }
    } catch (error) {
      logError('Failed to load markets');
    }
  };

  const loadProducts = async () => {
    if (!selectedInstance || !selectedMarket) return;
    
    setIsLoading(true);
    try {
      const filterOptions: MarketItemFilterOptions = {
        companyId: selectedInstance,
        customerTypeId: selectedMarket,
        searchTerm: searchTerm || undefined,
        pageNumber: currentPage,
        pageSize: pageSize,
      };

      const response = await client.getMarketItems(filterOptions);
      
      if (response.success && response.data) {
        setProducts(response.data.items || []);
        setTotalPages(response.data.totalPages || 0);
        setTotalItems(response.data.totalCount || 0);
      } else {
        setErrorMessage(response.message || t('loadErrorMessage'));
        setIsSubmittedSuccessfully(false);
        setShowSubmitStatusMessage(true);
      }
    } catch (error) {
      logError('Failed to load products');
      setErrorMessage(t('loadErrorMessage'));
      setIsSubmittedSuccessfully(false);
      setShowSubmitStatusMessage(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearchInputChange = (value: string) => {
    setSearchInput(value);
  };

  const handleSearchKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const handleSearch = () => {
    setSearchTerm(searchInput);
    setCurrentPage(0);
  };

  const handleInstanceChange = (event: SelectChangeEvent<string>) => {
    const newInstance = event.target.value;
    setSelectedInstance(newInstance);
    setSelectedMarket('');
    setCurrentPage(0);
    onFilterChange?.(newInstance, '');
  };

  const handleMarketChange = (event: SelectChangeEvent<string>) => {
    const newMarket = event.target.value;
    setSelectedMarket(newMarket);
    setCurrentPage(0);
    onFilterChange?.(selectedInstance, newMarket);
  };

  const handleClear = () => {
    setSearchInput('');
    setSearchTerm('');
    setSelectedInstance('');
    setSelectedMarket('');
    setCurrentPage(0);
    onFilterChange?.('', '');
  };

  const onClear = () => {
    handleClear();
  };

  const handleCloseMessage = () => {
    setShowSubmitStatusMessage(false);
  };

  const maxPages = () => {
    return Math.max(1, totalPages);
  };

  const showSelectMessage = !selectedInstance || !selectedMarket;

  return (
    <>
      <Grid container
        spacing={5}>
        <Grid item
          lg={3}
          sm={6}
          xs={12}>
          <TextField
            label={t('search')}
            placeholder={t('searchPlaceholder')}
            value={searchInput}
            onChange={(event) => handleSearchInputChange(event.target.value)}
            onKeyPress={handleSearchKeyPress}
            fullWidth
            disabled={showSelectMessage}
          />
        </Grid>
        <Grid item
          lg={2}
          sm={3}
          xs={6}>
          <Button
            fullWidth
            onClick={handleClear}
            variant='secondary'
            size='lg'
            disabled={showSelectMessage}
          >
            <Typography>{t('clear')}</Typography>
          </Button>
        </Grid>
        <Grid item
          lg={2}
          sm={3}
          xs={6}>
          <Button
            fullWidth
            onClick={handleSearch}
            size='lg'
            startIcon={<SvgIcon><SearchLg /></SvgIcon>}
            disabled={showSelectMessage}
          >
            <Typography>{t('search')}</Typography>
          </Button>
        </Grid>
        <Grid item
          lg={2.5}
          sm={6}
          xs={12}>
          <FormControl fullWidth>
            <InputLabel shrink>
              {t('filterByInstance')}
            </InputLabel>
            <Select
              value={selectedInstance}
              onChange={handleInstanceChange}
              label={t('filterByInstance')}
              displayEmpty
              notched
            >
              <MenuItem value="">
                <em>{t('selectInstance')}</em>
              </MenuItem>
              {instances.map((instance) => (
                <MenuItem key={instance.id}
                  value={instance.id.toString()}>
                  {instance.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item
          lg={2.5}
          sm={6}
          xs={12}>
          <FormControl fullWidth>
            <InputLabel shrink>
              {t('filterByMarket')}
            </InputLabel>
            <Select
              value={selectedMarket}
              onChange={handleMarketChange}
              label={t('filterByMarket')}
              displayEmpty
              notched
              disabled={!selectedInstance}
            >
              <MenuItem value="">
                <em>{t('selectMarket')}</em>
              </MenuItem>
              {markets.map((market) => (
                <MenuItem key={market.customerTypeId}
                  value={market.customerTypeId.toString()}>
                  {market.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item
          xs={12}>
          <Section>
            {showSelectMessage ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: '700px',
                  textAlign: 'center',
                }}
              >
                <Typography variant="textMD"
                  color="text.secondary"
                  gutterBottom>
                  {t('selectInstanceAndMarket')}
                </Typography>
              </Box>
            ) : (
              <>
                <ProductsList
                  list={products}
                  isLoading={isLoading}
                  totalCount={totalItems}
                  onClear={onClear}
                  onCreateNew={onCreateNew}
                />
                <Pagination
                  page={currentPage}
                  setPage={setCurrentPage}
                  maxPages={maxPages()}
                  breakpointForChangeDisplay={120000}
                  useNoDataMessage={false}
                />
              </>
            )}
          </Section>
        </Grid>
      </Grid>

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={showSubmitStatusMessage}
        onClose={handleCloseMessage}
        autoHideDuration={5000}
      >
        <Alert
          onClose={handleCloseMessage}
          severity={isSubmittedSuccessfully ? 'success' : 'error'}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {isSubmittedSuccessfully ? (errorMessage || t('successMessage')) : errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ProductsTab;
